import { ChangeEvent, Dispatch, SetStateAction } from 'react';
import { TNullable } from '@common/types';
import { PDF_ZOOM_SCALE } from '@constants/standard-pdf';
import { LogOut } from 'lucide-react';
import { PanelRight, ZoomIn, ZoomOut, Redo, Undo } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TChapterInfo } from './pdf.models';

interface IMobilePDFHeaderProps {
  pdfName: string;
  pageInputValue: number | '';
  handlePageChange: (operation: 'prev' | 'next') => void;
  handleSetPage: (e: ChangeEvent<HTMLInputElement>) => void;
  handleZoomIn: () => void;
  handleZoomOut: () => void;
  scale: number;
  setPageInputValue: (value: SetStateAction<number | ''>) => void;
  selectedPage: number;
  totalPages: number;
  chapters: TChapterInfo[];
  selectedChapter: TNullable<TChapterInfo>;
  handleChapterChange: (chapterNumber: string) => void;
  setChapterNavigatorOpen: Dispatch<SetStateAction<boolean>>;
}

function MobilePDFHeader(props: IMobilePDFHeaderProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  function handleBack() {
    navigate(-1);
  }

  return (
    <header className="fixed w-full left-0 top-0 z-50 bg-xircle-main text-[#eee] shadow-md flex flex-col justify-start items-start gap-1 h-20 py-2">
      <section className="w-full flex justify-between items-center gap-3 px-2">
        <span className="pl-2 text-sm">{props.pdfName}</span>
        <section
          className="flex items-center gap-1 bg-xircle-secondary py-1 px-2 rounded-md"
          onClick={handleBack}
        >
          <LogOut size={16} />
          <span className="text-sm">{t('common.back')}</span>
        </section>
      </section>
      <section className="toolbar bg-xircle-secondary w-full px-2 py-1">
        <section className="text-[#eee] flex justify-between items-center">
          <div className="page-controls flex justify-start items-center gap-2">
            <input
              className="w-9 h-9 text-center font-bold text-black/70 text-xs border border-gray-700 outline-none rounded-full"
              value={props.pageInputValue}
              onChange={props.handleSetPage}
              onBlur={() => props.setPageInputValue(props.selectedPage)}
            />
            <span className="text-lg font-bold border-b-2 border-[#eee]">{props.totalPages}</span>
          </div>
          <div className="pdf-controls flex justify-start items-center gap-4">
            <div className="pdf-control flex justify-between items-center gap-4">
              <button
                className="flex justify-center items-center cursor-pointer rounded-full disabled:text-slate-400"
                onClick={() => props.handlePageChange('prev')}
                disabled={props.selectedPage === 1}
                title={t('pdf.prev')}
              >
                <Undo size={20} />
              </button>
              <button
                className="flex justify-center items-center cursor-pointer rounded-full disabled:text-slate-400"
                onClick={() => props.handlePageChange('next')}
                disabled={props.selectedPage === props.totalPages}
                title={t('pdf.next')}
              >
                <Redo size={20} />
              </button>
            </div>
            <div className="pdf-control flex justify-start items-center gap-4">
              <button
                className="flex justify-center items-center cursor-pointer rounded-full disabled:text-slate-400"
                onClick={props.handleZoomIn}
                disabled={props.scale >= PDF_ZOOM_SCALE.MAX}
                title={t('pdf.zoom_in')}
              >
                <ZoomIn size={20} />
              </button>
              <button
                className="flex justify-center items-center cursor-pointer rounded-full disabled:text-slate-400"
                onClick={props.handleZoomOut}
                disabled={props.scale <= PDF_ZOOM_SCALE.MIN}
                title={t('pdf.zoom_out')}
              >
                <ZoomOut size={20} />
              </button>
            </div>
          </div>
          <div className="pdf-control flex justify-start items-center">
            <PanelRight
              className="cursor-pointer"
              onClick={() => props.setChapterNavigatorOpen((prev) => !prev)}
            />
          </div>
        </section>
      </section>
    </header>
  );
}

export default MobilePDFHeader;
